// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	getGeneratorVideoCtor,
	getNativeScreenCaptureApi,
	getVideoFrameCtor,
	isValidVideoFrameMessage,
	MAX_GENERATOR_PENDING_VIDEO_FRAMES,
	type NativeScreenBridgeBootstrap,
	type NativeScreenBridgeHandle,
	normalizeNativeScreenCaptureDimension,
	nv12VideoFrameLayout,
	type RendererVideoFrame,
	type VideoFrameCtor,
} from '@app/features/voice/utils/native_screen_capture_bridge/shared';
import type {NativeScreenCaptureFrameMessage} from '@app/types/electron.d';

const logger = new Logger('NativeScreenCaptureBridge');
const FALLBACK_NATIVE_SCREEN_FRAME_TIMESTAMP_US = 0;

function isUsableFrameDataUrl(value?: string | null): value is string {
	if (!value) {
		return false;
	}
	const trimmedValue = value.trim();
	if (!trimmedValue.startsWith('data:image/')) {
		return false;
	}
	const base64MarkerIndex = trimmedValue.indexOf('base64,');
	return base64MarkerIndex >= 0 && trimmedValue.length > base64MarkerIndex + 'base64,'.length;
}

function createNativeScreenFallbackCanvas(width: number, height: number): HTMLCanvasElement | OffscreenCanvas | null {
	if (typeof OffscreenCanvas !== 'undefined') {
		return new OffscreenCanvas(width, height);
	}
	if (typeof document === 'undefined') {
		return null;
	}
	const canvas = document.createElement('canvas');
	canvas.width = width;
	canvas.height = height;
	return canvas;
}

function createSolidNativeScreenFallbackFrame(
	width: number,
	height: number,
	VideoFrame: VideoFrameCtor,
): RendererVideoFrame {
	const yBytes = width * height;
	const data = new Uint8Array((width * height * 3) / 2);
	data.fill(16, 0, yBytes);
	data.fill(128, yBytes);
	return new VideoFrame(data, {
		format: 'NV12',
		codedWidth: width,
		codedHeight: height,
		displayWidth: width,
		displayHeight: height,
		timestamp: FALLBACK_NATIVE_SCREEN_FRAME_TIMESTAMP_US,
		layout: nv12VideoFrameLayout(width, height),
	});
}

async function createThumbnailNativeScreenFallbackFrame(
	dataUrl: string | undefined,
	width: number,
	height: number,
	VideoFrame: VideoFrameCtor,
): Promise<RendererVideoFrame | null> {
	if (!isUsableFrameDataUrl(dataUrl) || typeof fetch === 'undefined' || typeof createImageBitmap === 'undefined') {
		return null;
	}
	let bitmap: ImageBitmap | null = null;
	try {
		const response = await fetch(dataUrl);
		const blob = await response.blob();
		bitmap = await createImageBitmap(blob);
		const canvas = createNativeScreenFallbackCanvas(width, height);
		if (!canvas) {
			return null;
		}
		const context = canvas instanceof OffscreenCanvas ? canvas.getContext('2d') : canvas.getContext('2d');
		if (!context) {
			return null;
		}
		context.drawImage(bitmap, 0, 0, width, height);
		return new VideoFrame(canvas, {
			timestamp: FALLBACK_NATIVE_SCREEN_FRAME_TIMESTAMP_US,
			displayWidth: width,
			displayHeight: height,
		});
	} catch (error) {
		logger.warn('Failed to construct native screen fallback frame from thumbnail', {error});
		return null;
	} finally {
		try {
			bitmap?.close();
		} catch {}
	}
}

async function createNativeScreenFallbackFrame(
	bootstrap: NativeScreenBridgeBootstrap,
	VideoFrame: VideoFrameCtor,
): Promise<RendererVideoFrame | null> {
	const width = normalizeNativeScreenCaptureDimension(bootstrap.fallbackWidth);
	const height = normalizeNativeScreenCaptureDimension(bootstrap.fallbackHeight);
	if (!width || !height) {
		return null;
	}
	const thumbnailFrame = await createThumbnailNativeScreenFallbackFrame(
		bootstrap.fallbackThumbnailDataUrl,
		width,
		height,
		VideoFrame,
	);
	if (thumbnailFrame) {
		return thumbnailFrame;
	}
	try {
		return createSolidNativeScreenFallbackFrame(width, height, VideoFrame);
	} catch (error) {
		logger.warn('Failed to construct solid native screen fallback frame', {error});
		return null;
	}
}

export async function createScreenGeneratorBridge(
	captureId: string,
	bootstrap: NativeScreenBridgeBootstrap = {},
): Promise<NativeScreenBridgeHandle> {
	const nativeScreenCaptureApi = getNativeScreenCaptureApi();
	const Generator = getGeneratorVideoCtor();
	const VideoFrame = getVideoFrameCtor();
	if (!nativeScreenCaptureApi || !Generator || !VideoFrame) {
		throw new Error('MediaStreamTrackGenerator path unavailable for native screen capture');
	}
	const generator = new Generator({kind: 'video'});
	const writer = generator.writable.getWriter();
	let cleanedUp = false;
	let activeWrite: Promise<void> | null = null;
	const pendingFrames: Array<RendererVideoFrame> = [];
	let hasQueuedFrame = false;
	const closeFrame = (frame: RendererVideoFrame): void => {
		try {
			frame.close();
		} catch {}
	};
	const enqueueVideoFrame = (videoFrame: RendererVideoFrame): void => {
		if (cleanedUp) {
			closeFrame(videoFrame);
			return;
		}
		hasQueuedFrame = true;
		pendingFrames.push(videoFrame);
		while (pendingFrames.length > MAX_GENERATOR_PENDING_VIDEO_FRAMES) {
			const dropped = pendingFrames.shift();
			if (dropped) closeFrame(dropped);
		}
		pumpPendingFrames();
	};
	const pumpPendingFrames = (): void => {
		if (cleanedUp || activeWrite || pendingFrames.length === 0) return;
		const videoFrame = pendingFrames.shift()!;
		try {
			activeWrite = writer
				.write(videoFrame)
				.catch((error) => {
					logger.warn('Failed to write native screen frame to MediaStreamTrackGenerator', {captureId, error});
				})
				.finally(() => {
					closeFrame(videoFrame);
					activeWrite = null;
					pumpPendingFrames();
				});
		} catch (error) {
			logger.warn('Failed to write native screen frame to MediaStreamTrackGenerator', {captureId, error});
			closeFrame(videoFrame);
			activeWrite = null;
			pumpPendingFrames();
		}
	};
	const cleanup = async (stopRemote: boolean = true): Promise<void> => {
		if (cleanedUp) return;
		cleanedUp = true;
		unsubscribeFrame();
		unsubscribeEnd();
		for (const videoFrame of pendingFrames.splice(0)) {
			closeFrame(videoFrame);
		}
		const stopRemotePromise = stopRemote
			? nativeScreenCaptureApi.stop(captureId).catch((error) => {
					logger.warn('Failed to stop native screen capture after generator cleanup', {captureId, error});
				})
			: null;
		if (activeWrite) {
			await activeWrite;
		}
		try {
			await writer.close();
		} catch {}
		try {
			generator.stop();
		} catch {}
		try {
			generator.dispatchEvent(new Event('ended'));
		} catch {}
		await stopRemotePromise;
	};
	const enqueueFrameMessage = (message: NativeScreenCaptureFrameMessage): void => {
		if (message.captureId !== captureId || cleanedUp) return;
		if (!isValidVideoFrameMessage(message)) {
			logger.warn('Dropping invalid native screen capture frame', {
				captureId,
				format: message.format,
				width: message.width,
				height: message.height,
				byteLength: message.data?.byteLength,
			});
			return;
		}
		let videoFrame: RendererVideoFrame;
		try {
			const view = new Uint8Array(message.data);
			const timestamp = Math.max(0, Math.round(message.timestampUs));
			if (message.format === 'nv12') {
				videoFrame = new VideoFrame(view, {
					format: 'NV12',
					codedWidth: message.width,
					codedHeight: message.height,
					displayWidth: message.width,
					displayHeight: message.height,
					timestamp,
					layout: nv12VideoFrameLayout(message.width, message.height, message.strideY, message.strideUV),
				});
			} else {
				const bgraStride = message.strideY >= message.width * 4 ? message.strideY : message.width * 4;
				videoFrame = new VideoFrame(view, {
					format: 'BGRA',
					codedWidth: message.width,
					codedHeight: message.height,
					displayWidth: message.width,
					displayHeight: message.height,
					timestamp,
					layout: [{offset: 0, stride: bgraStride}],
				});
			}
		} catch (error) {
			logger.warn('Failed to construct VideoFrame for MediaStreamTrackGenerator', {captureId, error});
			return;
		}
		enqueueVideoFrame(videoFrame);
	};
	const unsubscribeFrame = nativeScreenCaptureApi.onFrame(enqueueFrameMessage);
	const unsubscribeEnd = nativeScreenCaptureApi.onEnd((message) => {
		if (message.captureId !== captureId) return;
		void cleanup(false);
	});
	if (bootstrap.initialFrame) {
		enqueueFrameMessage(bootstrap.initialFrame);
	} else {
		const fallbackFrame = await createNativeScreenFallbackFrame(bootstrap, VideoFrame);
		if (fallbackFrame) {
			if (hasQueuedFrame || cleanedUp) {
				closeFrame(fallbackFrame);
			} else {
				enqueueVideoFrame(fallbackFrame);
			}
		}
	}
	return {
		track: generator,
		cleanup,
	};
}
